import { Mail, Phone, Instagram } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function Contact() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b bg-primary to-green-400/10">
      <Helmet>
        <title>Contact Aleks | Nutritional Therapist in Ealing</title>
        <meta
          name="description"
          content="Get in touch with Aleks, a registered nutritional therapist in Ealing, West London. Contact via email, phone, or Instagram for nutrition consultations and health advice."
        />
        <meta
          name="keywords"
          content="contact nutritionist, nutritional therapist contact, Ealing nutritionist, nutrition consultation contact"
        />
        <meta
          property="og:title"
          content="Contact Aleks - Nutritional Therapist"
        />
        <meta
          property="og:description"
          content="Reach out to book a consultation or ask questions about nutritional therapy services. Available via email, phone, and social media."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
        {/* Page Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            Get in Touch
          </h1>
          <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full"></div>
        </div>

        {/* Contact Information Cards */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Email */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="flex justify-center mb-4">
                <Mail className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Email</h3>
              <a
                href="mailto:aleksandra@nrwa.co.uk"
                className="text-base text-gray-700 hover:text-primary transition-colors duration-200 font-semibold break-all"
              >
                aleksandra@nrwa.co.uk
              </a>
            </div>

            {/* Phone */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="flex justify-center mb-4">
                <Phone className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Phone</h3>
              <a
                href="tel:07814618877"
                className="text-base text-gray-700 hover:text-primary transition-colors duration-200 font-semibold"
              >
                07814618877
              </a>
            </div>

            {/* Instagram */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="flex justify-center mb-4">
                <Instagram className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Instagram
              </h3>
              <a
                href="https://www.instagram.com/nutriresultswithaleks"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base text-gray-700 hover:text-primary transition-colors duration-200 font-semibold"
              >
                @nutriresultswithaleks
              </a>
            </div>
          </div>
        </div>

        {/* Book Discovery Call Section */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-10 lg:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Book Your Free Discovery Call
            </h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-8 text-center">
              Ready to start your nutritional therapy journey? Book a
              complimentary 20-minute discovery call to discuss your health
              goals and how I can help you achieve optimal wellness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() =>
                  window.open(
                    "https://www.bookable.page/aleksandra",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="bg-primary hover:bg-[#245940] text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-base sm:text-lg"
              >
                Schedule Now
              </button>
              <button
                onClick={() => navigate("/nutrition-therapy")}
                className="bg-white hover:bg-gray-100 text-primary font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-base sm:text-lg border-2 border-primary"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>

        {/* Response Time Message */}
        <div className="max-w-4xl mx-auto mt-8 text-center">
          <p className="text-white/80 text-sm sm:text-base">
            I aim to respond to all inquiries within 24 hours
          </p>
        </div>
      </div>
    </div>
  );
}
